import { CommonModule } from '@angular/common';
import { Component, computed, inject } from '@angular/core';
import {
  HsButtonModule,
  HsFormFieldModule,
  HsInputModule,
  HsSearchBarModule,
} from '@hsetu/ngui-components';
import { HsLayerDirective } from '@hsetu/ngui-resources';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { ArticleItem } from '@modules/shopping/models/article-item.model';
import { GuidGeneratorService } from '@shared/tools/guid-generator.service';
import {
  CANCEL_LABEL,
  OK_LABEL,
  SHORT_TEXT_LABEL,
} from '@modules/shopping/i18n/label-text-constants';

@Component({
  selector: 'kfm-text-article-dialog',
  standalone: true,
  imports: [
    CommonModule,
    HsSearchBarModule,
    HsLayerDirective,
    HsButtonModule,
    HsInputModule,
    HsFormFieldModule,
  ],
  templateUrl: './kfm-text-article-dialog.component.html',
})
export class KfmTextArticleDialogComponent {
  private readonly _data = inject(DIALOG_DATA);
  private readonly _guidGenerator = inject(GuidGeneratorService);

  protected readonly cancelLabel = CANCEL_LABEL;
  protected readonly okLabel = OK_LABEL;
  protected readonly shortTextLabel = SHORT_TEXT_LABEL;

  protected readonly dialogRef = inject<DialogRef<ArticleItem>>(
    DialogRef<ArticleItem>,
  );

  protected selectedArticle: ArticleItem | undefined = undefined;

  protected get canSave(): boolean {
    return (
      this.selectedArticle &&
      this.selectedArticle.infoDesc &&
      this.selectedArticle.infoDesc.trim().length > 0
    );
  }

  protected readonly title = computed(() => {
    return this.selectedArticle ? this.selectedArticle.infoDesc : '';
  });

  constructor() {
    if (this._data && this._data['article']) {
      // Create a copy to avoid mutating the input data directly
      this.selectedArticle = { ...this._data['article'] };
    } else {
      this.selectedArticle = {
        uId: this._guidGenerator.emptyUUID,
        tenantUId: this._guidGenerator.emptyUUID,
        userUId: this._guidGenerator.emptyUUID,
        concurrencyIdentificator: this._guidGenerator.emptyUUID,
        syncType: 0,
        concurrencyStamp: new Date().toISOString(),
        stampCreate: new Date().toISOString(),
        amount: 1,
        unit: '',
        taskId: '',
        infoDesc: '',
      };
    }
  }

  protected onTitleChanged(newTitle: string): void {
    if (this.selectedArticle) {
      this.selectedArticle.infoDesc = newTitle.trim();
    }
  }

  protected onArticleNumberChanged(newArticleNumber: string): void {
    if (this.selectedArticle) {
      this.selectedArticle.articleNumber = newArticleNumber.trim();
    }
  }

  protected onAmountChanged(newAmount: string): void {
    if (this.selectedArticle) {
      const parsedAmount = parseFloat(newAmount);
      this.selectedArticle.amount = isNaN(parsedAmount) ? 0 : parsedAmount;
    }
  }

  protected onUnitChanged(newUnit: string): void {
    if (this.selectedArticle) {
      this.selectedArticle.unit = newUnit.trim();
    }
  }

  protected onUnitPriceChanged(newUnitPrice: string): void {
    if (this.selectedArticle) {
      const parsedUnitPrice = parseFloat(newUnitPrice);
      if (!this.selectedArticle.priceTotal) {
        this.selectedArticle.priceTotal = {};
      }
      this.selectedArticle.priceTotal.netTotal = isNaN(parsedUnitPrice)
        ? 0
        : parsedUnitPrice;
    }
  }
}
